import React from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button';
import type DropdownMenuType from '@atlaskit/dropdown-menu';
import { type OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import { media } from '@atlaskit/primitives/responsive';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { navLayout } from '@/patterns/global-nav/src/nav-layout';
import { useGlobalNavContext } from '@/patterns/global-nav/src/nav-links-container';

import { messages } from '../messages';
import { fontFamily } from '../util';

import { WACAvatar } from './avatar';
import { LoadingSpinner } from './spinner';
import { UserDetailsCard } from './user-details-card';

const DropdownMenu = React.lazy(() =>
	import('./dropdown-import.tsx').then(({ DropdownMenu }) => ({
		default: DropdownMenu,
	})),
) as unknown as typeof DropdownMenuType;

const DropdownItem = React.lazy(() =>
	import('@atlaskit/dropdown-menu').then(({ DropdownItem }) => ({
		default: DropdownItem,
	})),
);

const DropdownItemGroup = React.lazy(() =>
	import('@atlaskit/dropdown-menu').then(({ DropdownItemGroup }) => ({
		default: DropdownItemGroup,
	})),
);

export interface LoggedInUserDropdownProps {
	userInfo: {
		readonly displayName: string;
		readonly email: string;
		readonly avatar: string;
	};
	readonly onLogOutClick: () => void;
	readonly onManageLicensesClick: () => void;
	readonly onManageProfileClick: () => void;
	readonly onSwitchAccountClick: () => void;
}

export interface LoggedInUserProps extends LoggedInUserDropdownProps {
	readonly isUseNewLoggedInUserDropdown?: boolean;
}

export const LegacyLoggedInUseDropdown = ({
	userInfo,
	onLogOutClick,
	onManageLicensesClick,
	onManageProfileClick,
	onSwitchAccountClick,
}: LoggedInUserDropdownProps) => (
	<DropdownMenu
		shouldRenderToParent={true}
		spacing="compact"
		placement="bottom-end"
		trigger={({ triggerRef, ...props }) => (
			<Button
				className="avatar-button"
				css={overrideAvatarButtonStyle}
				{...props}
				iconBefore={
					<WACAvatar
						name={userInfo.displayName}
						src={userInfo.avatar}
						size={'small'}
						showTooltip={true}
					/>
				}
				ref={triggerRef}
				style={{ backgroundColor: 'transparent' }}
			/>
		)}
	>
		<div css={loggedInUserContainer} data-testid="global-nav-user-profile-container-user-loggedin">
			<DropdownItemGroup>
				<DropdownItem isDisabled={true}>
					<UserDetailsCard
						name={userInfo.displayName}
						src={userInfo.avatar}
						email={userInfo.email}
						size={'small'}
					/>
				</DropdownItem>
				<DropdownItem onClick={onSwitchAccountClick}>
					<FormattedMessage {...messages.switchAccount} />
				</DropdownItem>
				<DropdownItem onClick={onManageProfileClick}>
					<FormattedMessage {...messages.profile} />
				</DropdownItem>
				<DropdownItem onClick={onManageLicensesClick}>
					<FormattedMessage {...messages.licenses} />
				</DropdownItem>
				<DropdownItem onClick={onLogOutClick}>
					<FormattedMessage {...messages.logOut} />
				</DropdownItem>
			</DropdownItemGroup>
		</div>
	</DropdownMenu>
);

export const LoggedInUseDropdown = ({
	userInfo,
	onLogOutClick,
	onManageLicensesClick,
	onManageProfileClick,
	onSwitchAccountClick,
}: LoggedInUserDropdownProps) => {
	const intl = useIntl();
	const { setIsLoggedInUserProfileDropdownOpen } = useGlobalNavContext();

	return (
		<DropdownMenu
			shouldRenderToParent={true}
			placement="bottom-end"
			onOpenChange={(attrs: OnOpenChangeArgs) => {
				setIsLoggedInUserProfileDropdownOpen(attrs.isOpen);
			}}
			// eslint-disable-next-line react/no-unstable-nested-components
			trigger={({ triggerRef, ...props }) => (
				<Button
					className="avatar-button"
					{...props}
					css={profileTriggerStyle}
					aria-label={intl.formatMessage(messages.logOutTriggerText)}
					iconBefore={
						<WACAvatar
							name={userInfo.displayName}
							src={userInfo.avatar}
							size={'medium'}
							showTooltip={true}
						/>
					}
					ref={triggerRef}
					style={{
						height: 'var(--nav-profile-trigger-height, 32px)',
						backgroundColor: 'transparent',
						padding: '0px',
						fontSize: '.875rem',
						fontWeight: '600',
						alignItems: 'center',
						gap: '4px',
					}}
				/>
			)}
		>
			<div
				css={updatedLoggedInUserContainer}
				data-testid="global-nav-user-profile-container-user-loggedin"
			>
				<DropdownItemGroup css={dropdownListStyle}>
					<DropdownItem isDisabled={true}>
						<span css={disabledItemStyle}>{userInfo.email}</span>
					</DropdownItem>
					<DropdownItem onClick={onSwitchAccountClick}>
						<span css={dropdownItemStyle}>
							<FormattedMessage {...messages.switchAccount} />
						</span>
					</DropdownItem>
					<DropdownItem onClick={onManageProfileClick}>
						<span css={dropdownItemStyle}>
							<FormattedMessage {...messages.profile} />
						</span>
					</DropdownItem>
					<DropdownItem onClick={onManageLicensesClick}>
						<span css={dropdownItemStyle}>
							<FormattedMessage {...messages.licenses} />
						</span>
					</DropdownItem>
					<DropdownItem onClick={onLogOutClick}>
						<span css={dropdownItemStyle}>
							<FormattedMessage {...messages.logOut} />
						</span>
					</DropdownItem>
				</DropdownItemGroup>
			</div>
		</DropdownMenu>
	);
};

export const LoggedInUser = ({
	userInfo,
	onLogOutClick,
	onManageLicensesClick,
	onManageProfileClick,
	onSwitchAccountClick,
	isUseNewLoggedInUserDropdown = false,
}: LoggedInUserProps) => {
	return (
		<Suspense fallback={LoadingSpinner} interactionName={'loggedin-user-view'}>
			<div data-testid="main-container" css={mainContainer}>
				{isUseNewLoggedInUserDropdown ? (
					<LoggedInUseDropdown
						userInfo={userInfo}
						onLogOutClick={onLogOutClick}
						onManageLicensesClick={onManageLicensesClick}
						onManageProfileClick={onManageProfileClick}
						onSwitchAccountClick={onSwitchAccountClick}
					/>
				) : (
					<LegacyLoggedInUseDropdown
						userInfo={userInfo}
						onLogOutClick={onLogOutClick}
						onManageLicensesClick={onManageLicensesClick}
						onManageProfileClick={onManageProfileClick}
						onSwitchAccountClick={onSwitchAccountClick}
					/>
				)}
			</div>
		</Suspense>
	);
};

const mainContainer = css({
	fontFamily: fontFamily,
});
const loggedInUserContainer = css({
	fontFamily: fontFamily,
	width: '250px',
	lineHeight: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div::before': {
		content: '',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'button[disabled]': {
		padding: token('space.0'),
		cursor: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`button[role="menuitem"]`]: {
		fontFamily: fontFamily,
		fontSize: token('space.150'),
	},
});

const updatedLoggedInUserContainer = css({
	width: '264px',
	padding: token('space.500'),
	lineHeight: token('space.200'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		padding: token('space.0'),
		minHeight: '12px',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`button[role="menuitem"]`]: {
		'&:hover': {
			backgroundColor: 'transparent',
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div::before': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div::after': {
		display: 'none',
	},
});

const dropdownListStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const disabledItemStyle = css({
	fontSize: '12px',
	fontWeight: '700',
	textTransform: 'uppercase',
	color: wacToken('color.primary.black'),
	lineHeight: '16px',
	fontFamily: wacToken('font.charlie.text'),
	display: 'block',
});

const dropdownItemStyle = css({
	fontSize: '14px',
	lineHeight: '24px',
	fontFamily: wacToken('font.charlie.text'),
	display: 'block',

	'&:hover': {
		color: wacToken('color.extended.blue700'),
	},
});

const overrideAvatarButtonStyle = css({
	padding: token('space.0'),
	width: token('space.250'),
	height: token('space.250'),
	borderRadius: '50%',
});

const profileTriggerStyle = css({
	// DropdownMenu adds an 8px gap below its trigger. Center a 60px trigger
	// in the 68px nav so the popup starts 4px below the nav's bottom edge.
	[media.above.md]: {
		'--nav-profile-trigger-height': `calc(${navLayout.height} - ${token('space.100', '8px')})`,
	},
});
